import { Color, Group, Sprite, SpriteMaterial, Vector3 } from 'three';
import { clamp } from '../utils/math.js';
import { getSmokeTexture } from '../utils/textures.js';

const LIGHT_SMOKE = new Color(0xf2eee6);
const HEAVY_SMOKE = new Color(0xb9b3a8);

const DEFAULTS = {
  threshold: 0.8,
  maxPuffs: 32,
  rateMin: 1.5,
  rateMax: 14,
  lifeMin: 1.15,
  lifeMax: 2.0,
};

/**
 * Hood smoke for the player's truck.
 *
 * Below `threshold` (80% health by default) the engine starts to smoke; the
 * emission rate, puff size and darkness all climb as health falls to zero.
 * Puffs live in world space so they trail behind the moving truck instead of
 * sticking to the bonnet. Updated from `Vehicle.render` so the plume keeps
 * drifting even while the game-over screen is up.
 */
export class VehicleSmoke {
  constructor({ parent, config = {} }) {
    this.config = { ...DEFAULTS, ...config };
    this.group = new Group();
    this.group.name = 'vehicle-hood-smoke';
    parent.add(this.group);

    this.puffs = [];
    this.severity = 0;
    this.rate = 0;

    this._accumulator = 0;
    this._hood = new Vector3();
    this._forward = new Vector3();
    this._color = new Color();
  }

  get activeCount() {
    let count = 0;
    for (const puff of this.puffs) {
      if (puff.active) count += 1;
    }
    return count;
  }

  /**
   * @param {number} dt seconds since the last rendered frame
   * @param {import('./Vehicle.js').Vehicle} vehicle
   */
  update(dt, vehicle) {
    const step = clamp(dt, 0, 0.1);
    const maxHealth = Math.max(1, vehicle.maxHealth ?? 1);
    const fraction = clamp(vehicle.health / maxHealth, 0, 1);
    const threshold = this.config.threshold;

    const severity = fraction < threshold
      ? clamp((threshold - fraction) / threshold, 0, 1)
      : 0;
    this.severity = severity;
    this.rate = severity > 0
      ? this.config.rateMin + (this.config.rateMax - this.config.rateMin) * severity
      : 0;

    this.#advance(step);

    if (severity <= 0) {
      this._accumulator = 0;
      return;
    }
    if (step <= 0) return;

    this._accumulator += this.rate * step;
    let spawned = 0;
    while (this._accumulator >= 1 && spawned < 6) {
      this.#spawn(vehicle, severity);
      this._accumulator -= 1;
      spawned += 1;
    }
  }

  /** Stop emitting and hide every live puff (repair / respawn). */
  reset() {
    for (const puff of this.puffs) this.#retire(puff);
    this._accumulator = 0;
    this.severity = 0;
    this.rate = 0;
  }

  dispose() {
    for (const puff of this.puffs) puff.material.dispose();
    this.puffs.length = 0;
    this.group.removeFromParent();
  }

  #advance(dt) {
    for (const puff of this.puffs) {
      if (!puff.active) continue;

      puff.life += dt;
      const t = puff.life / puff.maxLife;
      if (t >= 1) {
        this.#retire(puff);
        continue;
      }

      // Smoke slows and rises as it ages.
      const drag = Math.max(0, 1 - 0.7 * dt);
      puff.velocity.x *= drag;
      puff.velocity.z *= drag;
      puff.velocity.y += 0.25 * dt;
      puff.sprite.position.addScaledVector(puff.velocity, dt);

      const growth = 1 - (1 - t) * (1 - t);
      puff.sprite.scale.setScalar(
        puff.startScale + (puff.endScale - puff.startScale) * growth,
      );
      puff.material.opacity = puff.peakOpacity * Math.sin(Math.PI * t);
      puff.material.rotation += puff.spin * dt;
    }
  }

  #spawn(vehicle, severity) {
    const puff = this.#acquire();
    const mesh = vehicle.mesh;

    // Bonnet seam, a little ahead of the windscreen. Local +Z is the nose.
    this._hood.set(
      (Math.random() - 0.5) * 0.85,
      0.4 + Math.random() * 0.1,
      1.32 + Math.random() * 0.42,
    );
    this._hood.applyQuaternion(mesh.quaternion).add(mesh.position);
    puff.sprite.position.copy(this._hood);

    this._forward.set(0, 0, 1).applyQuaternion(mesh.quaternion);
    this._forward.y = 0;
    if (this._forward.lengthSq() < 1e-5) this._forward.set(0, 0, 1);
    else this._forward.normalize();

    // A little of the truck's speed carries the puff before it is left behind.
    const speed = vehicle.focus?.speed ?? 0;
    puff.velocity
      .copy(this._forward)
      .multiplyScalar(speed * 0.25 - (0.2 + Math.random() * 0.55));
    puff.velocity.x += (Math.random() - 0.5) * 0.5;
    puff.velocity.y = 0.45 + Math.random() * 0.55;
    puff.velocity.z += (Math.random() - 0.5) * 0.5;

    puff.life = 0;
    puff.maxLife =
      this.config.lifeMin
      + Math.random() * (this.config.lifeMax - this.config.lifeMin)
      + severity * 0.2;
    puff.startScale = 0.22 + severity * 0.18 + Math.random() * 0.1;
    puff.endScale = 0.85 + severity * 0.75 + Math.random() * 0.35;
    puff.peakOpacity = 0.65 + severity * 0.3;
    puff.spin = (Math.random() - 0.5) * 1.4;
    puff.material.color.copy(
      this._color.copy(LIGHT_SMOKE).lerp(HEAVY_SMOKE, severity),
    );
    puff.material.rotation = Math.random() * Math.PI * 2;
    puff.material.opacity = 0;
    puff.sprite.scale.setScalar(puff.startScale);
    puff.sprite.visible = true;
    puff.active = true;
  }

  #acquire() {
    for (const puff of this.puffs) {
      if (!puff.active) return puff;
    }
    if (this.puffs.length < this.config.maxPuffs) {
      const puff = this.#createPuff();
      this.puffs.push(puff);
      return puff;
    }
    // Pool is full: recycle the oldest live puff.
    return this.puffs.reduce(
      (oldest, puff) => (puff.life > oldest.life ? puff : oldest),
      this.puffs[0],
    );
  }

  #createPuff() {
    const material = new SpriteMaterial({
      map: getSmokeTexture(),
      color: LIGHT_SMOKE,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    const sprite = new Sprite(material);
    sprite.visible = false;
    this.group.add(sprite);
    return {
      sprite,
      material,
      active: false,
      life: 0,
      maxLife: 1,
      startScale: 0.3,
      endScale: 1,
      peakOpacity: 0.3,
      spin: 0,
      velocity: new Vector3(),
    };
  }

  #retire(puff) {
    puff.active = false;
    puff.life = 0;
    puff.material.opacity = 0;
    puff.sprite.visible = false;
  }
}
